import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { TrendingUp, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STATS = [
  { k: 'revenue', l: 'Faturamento' },
  { k: 'commission', l: 'Comissão' },
  { k: 'sales', l: 'Vendas' },
  { k: 'scheduled', l: 'Agendamentos (qtd)' },
  { k: 'scheduledValue', l: 'Agendamentos (R$)' },
];
const PERIODS = [{ v: 7, l: '7 dias' }, { v: 14, l: '14 dias' }, { v: 30, l: '30 dias' }, { v: 60, l: '60 dias' }, { v: 90, l: '90 dias' }];
const COLORS = ['#22D3EE', '#4ADE80', '#FACC15', '#F87171', '#A78BFA', '#FB923C', '#38BDF8', '#F472B6'];

const MultiChip = ({ options, selected, onToggle }) => (
  <div className="flex flex-wrap gap-1">
    {options.map((o) => (
      <button key={o.k} onClick={() => onToggle(o.k)}
        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${selected.includes(o.k) ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'}`}>
        {o.l}
      </button>
    ))}
  </div>
);

const SellerTimelineChart = ({ endpoint = '/sellers/timeline', title = 'Desempenho ao longo do tempo', extraParams = '', seriesLabel = 'Vendedores (múltipla seleção)', selectedIds = null }) => {
  const [days, setDays] = useState(14);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selSellers, setSelSellers] = useState([]);
  const [selStats, setSelStats] = useState(['revenue']);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`${endpoint}?days=${days}${extraParams}`)
      .then((r) => {
        if (!alive || !r.data?.success) return;
        setData(r.data.series || []);
        if (selectedIds && selectedIds.length) setSelSellers(selectedIds);
        else setSelSellers((prev) => prev.length ? prev : (r.data.series || []).slice(0, 3).map(s => s.id));
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [days, endpoint, extraParams]);

  useEffect(() => { if (selectedIds && selectedIds.length) setSelSellers(selectedIds); }, [JSON.stringify(selectedIds)]);

  const toggle = (setter) => (k) => setter((prev) => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  const chartData = useMemo(() => {
    const daysList = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
      d.setDate(d.getDate() - i);
      daysList.push(d.toISOString().split('T')[0]);
    }
    return daysList.map((day) => {
      const row = { day: day.slice(5) };
      data.filter(s => selSellers.includes(s.id)).forEach((s) => {
        selStats.forEach((stat) => {
          row[`${s.name} · ${(STATS.find(x => x.k === stat) || {}).l}`] = (s.byDay[day] || {})[stat] || 0;
        });
      });
      return row;
    });
  }, [data, selSellers, selStats, days]);

  const lineKeys = useMemo(() => {
    const keys = [];
    data.filter(s => selSellers.includes(s.id)).forEach((s) => {
      selStats.forEach((stat) => keys.push(`${s.name} · ${(STATS.find(x => x.k === stat) || {}).l}`));
    });
    return keys;
  }, [data, selSellers, selStats]);

  return (
    <div className="bg-gray-800 rounded-xl p-5 border border-gray-700 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} className="text-cyan-400" />
          <h3 className="text-white font-bold">{title}</h3>
        </div>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm">
          {PERIODS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
        </select>
      </div>
      <div className="space-y-2">
        <p className="text-gray-500 text-xs">{seriesLabel}</p>
        <MultiChip options={data.map(s => ({ k: s.id, l: s.name }))} selected={selSellers} onToggle={toggle(setSelSellers)} />
        <p className="text-gray-500 text-xs mt-2">Estatísticas (múltipla seleção)</p>
        <MultiChip options={STATS} selected={selStats} onToggle={toggle(setSelStats)} />
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-48 text-gray-500"><Loader2 className="animate-spin mr-2" size={18} /> Carregando...</div>
      ) : lineKeys.length === 0 ? (
        <div className="flex items-center justify-center h-48 text-gray-500 text-sm">Selecione ao menos um vendedor e uma estatística.</div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="day" stroke="#9CA3AF" fontSize={11} />
            <YAxis stroke="#9CA3AF" fontSize={11} />
            <Tooltip contentStyle={{ background: '#1F2937', border: '1px solid #374151', borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {lineKeys.map((k, i) => (
              <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default SellerTimelineChart;
